import { graphql, StaticQuery } from 'gatsby';
import React from 'react';
import styled from 'styled-components';

import mailIcon from '../../images/icons/contact-info/icon-contact-mail.svg';
import mapIcon from '../../images/icons/contact-info/icon-contact-map.svg';
import phoneIcon from '../../images/icons/contact-info/icon-contact-phone.svg';
import { queryResponse } from '../../interfaces/AllKindergartensQueryResponse';
import { decodeUri } from '../../utils/DecodeUri';
import {
  findKindergartenByAllKindergertensJson,
  Kindergarten,
} from '../../utils/FindKindergartenByAllKindergartensJson';
import { getKindergartenByPathname } from '../../utils/GetKindergartenByPathname';
import { theme } from '../../utils/Theme';

const ContactInfoWrapper = styled.div`
  height: 200px;
  background-color: ${theme.colors.darkGreen};
  display: flex;
  ${theme.mediaQuerySizes.phoneLight} {
    height: auto;
    display: block;
  }
`;

const StyledMapIcon = styled(mapIcon)``;
const StyledMailIcon = styled(mailIcon)``;
const StyledPhoneIcon = styled(phoneIcon)``;

const StyledText = styled.p`
  color: ${theme.colors.white};
  font-family: Recoleta-SemiBold;
  font-size: 1.5rem;
  margin-bottom: 0;
  transition: color 0.2s ease-out;

  ${theme.mediaQuerySizes.smallLaptop} {
    font-size: 1rem;
  }
  ${theme.mediaQuerySizes.tablet} {
    font-size: 0.88rem;
  }
  ${theme.mediaQuerySizes.phoneLight} {
    font-size: 1.1rem;
  }
`;

const ContactItem = styled.a`
  width: 33.3%;
  height: 100%;
  display: grid;
  justify-content: center;
  text-align: center;
  text-decoration: none;

  svg {
    height: 100px;
    margin: auto;
    polygon,
    path {
      fill: ${theme.colors.white};
      transition: fill 0.2s ease-out;
    }
  }

  :hover {
    svg polygon,
    svg path {
      fill: ${theme.colors.dark} !important;
    }
    ${StyledText} {
      color: ${theme.colors.dark};
    }
  }
  ${theme.mediaQuerySizes.phoneLight} {
    width: 100%;
    padding: 10px 0 10px 0;
  }
`;

const MiddleContactItem = styled(ContactItem)`
  z-index: 0;

  :before {
    background-color: rgba(0, 0, 0, 0.1);
    transform: skew(-25deg);
    content: '';
    position: absolute;
    width: 33%;
    height: 200px;
    z-index: -1;
  }
  ${theme.mediaQuerySizes.phone} {
    :before {
      transform: skew(-10deg);
    }
  }
  ${theme.mediaQuerySizes.phoneLight} {
    :before {
      transform: skew(0deg);
    }
  }
  ${theme.mediaQuerySizes.phoneLight} {
    width: 100%;
    padding: 10px 0 10px 0;
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

interface ContactInfoProps {
  location: {
    pathname: string;
  };
}

const googleMapsLink = (kindergartenNode: Kindergarten | undefined): string => {
  if (
    kindergartenNode &&
    kindergartenNode.geographicalCoordinates &&
    kindergartenNode.geographicalCoordinates.latitude &&
    kindergartenNode.geographicalCoordinates.latitude
  ) {
    return `https://www.google.com/maps/search/?api=1&query=
      ${kindergartenNode.geographicalCoordinates.latitude},
      ${kindergartenNode.geographicalCoordinates.longitude}`;
  } else {
    return '';
  }
};

const query = graphql`
  query {
    allKindergartensJson {
      nodes {
        email
        address
        firstPhone
        secondPhone
        name
        geographicalCoordinates {
          latitude
          longitude
        }
      }
    }
  }
`;

export const ContactInfo = ({ location }: ContactInfoProps) => {
  return (
    <StaticQuery
      query={query}
      render={(data: queryResponse<Kindergarten>) => {
        const kindergarten = findKindergartenByAllKindergertensJson(
          data,
          decodeUri(getKindergartenByPathname(location.pathname)),
        );
        return (
          <ContactInfoWrapper>
            <ContactItem href={kindergarten ? `tel:${kindergarten.firstPhone}` : ''}>
              <StyledPhoneIcon></StyledPhoneIcon>
              <StyledText>
                {kindergarten ? kindergarten.firstPhone : ''}
                <br />
                {kindergarten ? kindergarten.secondPhone : ''}
              </StyledText>
            </ContactItem>
            <MiddleContactItem href={googleMapsLink(kindergarten)}>
              <StyledMapIcon></StyledMapIcon>
              <StyledText>
                {kindergarten ? kindergarten.name : ''}
                <br />
                {kindergarten ? kindergarten.address : ''}
              </StyledText>
            </MiddleContactItem>
            <ContactItem href={kindergarten ? `mailto:${kindergarten.email}` : ''}>
              <StyledMailIcon></StyledMailIcon>
              <StyledText>{kindergarten ? kindergarten.email : ''}</StyledText>
            </ContactItem>
          </ContactInfoWrapper>
        );
      }}
    />
  );
};
